import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { LAYOUT_ACTIONS, LAYOUT_QUERIES, LayoutActions, LayoutQueries } from '@core/store/types';
import { Subscription } from 'rxjs';
import { USER_QUERIES, UserQueries } from '@/app/platform/platform-core/user/store/types';
import { PlatformAppContext } from '@/app/platform/platform-context/platform-app-context';
import { UserNavigationItem } from '@/app/platform/platform-core/user/models/user-navigation-item';
import { Location } from '@angular/common';
import { sleep } from '@common/utils/promise-utils';
import { ApplicationService } from '@/app/application.service';
import { SupportAccessService } from '@/app/support/support-access.service';
import { ViewportService } from '@core/services/viewport.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { PlatformModeService } from '@/app/platform/platform-core/platform-mode/platform-mode.service';

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    standalone: false
})
export class LayoutComponent implements OnInit, OnDestroy {
  constructor(
    @Inject(USER_QUERIES)
    readonly userQueries: UserQueries,
    @Inject(LAYOUT_QUERIES)
    public readonly layoutQueries: LayoutQueries,
    @Inject(LAYOUT_ACTIONS)
    readonly layoutActions: LayoutActions,
    readonly appContext: PlatformAppContext,
    private readonly location: Location,
    readonly appService: ApplicationService
  ) {}

  readonly supportAccess = inject(SupportAccessService);
  readonly platformMode = inject(PlatformModeService);
  private readonly viewport = inject(ViewportService);
  private readonly router = inject(Router);

  private subscriptions: Subscription[] = [];

  ngOnInit(): void {
    // Niet wachten op de hubverbinding: anders ontbreekt de uitleg even na het laden
    void this.platformMode.load();
    this.subscriptions.push(
      this.userQueries.authenticatedUser$.subscribe(user => {
        if (!user) {
          this.layoutActions.closeSideBar();
        }
      }),
      // Phone: de drawer overlapt de content — na een navigatie hoort hij dicht,
      // op desktop blijft hij open zoals de gebruiker hem zette.
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
        if (this.viewport.isPhone()) {
          this.layoutActions.closeSideBar();
        }
      }),
      // Fullscreen (navbar ingeklapt) als body-klasse: overlays buiten deze
      // component (bv. de phone-sheet van de floating panels) kunnen dan de
      // vrijgekomen hoogte benutten (docs/MOBIEL.md, transits op phone).
      this.layoutQueries.isFullscreen$.subscribe(fullscreen => {
        document.body.classList.toggle('layout-fullscreen', fullscreen);
      })
    );
  }

  onAfterLogin(): void {
    this.layoutActions.closeLoginDialog();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  async navigateToParent(activeNavigationItem: UserNavigationItem) {
    const previousLocation = location.href.split('&')[0];
    let currentLocation = previousLocation;

    const navigationId = window.history.state.navigationId;

    for (let i = navigationId; i > 0; i--) {
      this.location.back();
      await sleep(50); // needed to wait for the property on the location object to adjust
      currentLocation = location.href.split('&')[0];
      if (currentLocation !== previousLocation) {
        return;
      }
    }
  }
}
